import type { JSX } from 'react'
import type { PopupRowGap } from 'blocks/Popup'
import type { ISidebarComponentProps } from '../SidebarComponent'

import { InlineCloseButton } from 'atoms/Button/Templates'
import { Label, LabelFonts } from 'atoms/Label'
import { LabelLive } from 'atoms/Label/Templates/LabelLive'
import { PosterContainerBlur } from 'atoms/PosterContainerBlur'
import { TextBox, TextBoxTypes } from 'atoms/TextBox'
import {
  TextBoxBody4,
  TextBoxMaxLine1Headline3,
  TextBoxWithChildrenMaxLines2,
} from 'atoms/TextBox/Templates'
import { Colors } from 'atoms/TextBox/TextBox.h'
import { Animations } from 'blocks/AnimatedWrapper'
import { LabelGroup } from 'blocks/LabelGroup'
import { Divider } from 'blocks/List/List.h'
import { PopupHeaderSC } from 'blocks/Popup/Popup.styled'
import { CORNERS, FIXED_INDENTS, INDENTS, LAYOUT, SHADOW } from 'vars/index'
import { EpgPoster } from '../components/EpgPoster'
import { ProgramEpgItem } from '../components/ProgramEpgItem'
import { SidebarComponent } from '../SidebarComponent'
import { SidebarPositions } from '../SidebarComponent.h'
import {
  EpgSidebarBodySC,
  EpgSidebarProgramInfoSC,
  ProgramEpgList,
} from './EpgSidebarTemplate.styled'

export interface IEpgSidebarProps extends ISidebarComponentProps {
  channelLogoUrl?: string
  channelName: string

  rowGapDescription: PopupRowGap
  epgTitle: string
  programDescription: string
  isLoading: boolean
  isLive: boolean
  labels: string[]

  subtitle: string | null
  categories: string[] | null
  icon: string | null
  date: string | null
  rating: string | null
  directors: string[] | []
  actors: string[] | []
  tags: string[] | []
  episode: string | null

  programList?: []
  titleDirectors?: string
  titleStars?: string
  titleUpcoming?: string
  cornersHeader?: CORNERS
}

export const EpgSidebarTemplate = ({
  isOpen,
  transitionName = Animations.SlideInLeft300,
  position = SidebarPositions.Right,
  sidebarShadow = SHADOW.HUGE,
  horizontalPaddings = LAYOUT.HorizontalPaddings,

  channelLogoUrl,
  channelName,
  isCloseBtnShown,
  onClose,

  epgTitle,
  programDescription,
  isLoading = false,
  labels = [],
  isLive = false,
  subtitle,
  icon,
  directors,
  actors,
  episode,
  tags,

  programList,
  titleStars,
  titleDirectors,
  titleUpcoming,
  corners = CORNERS.CORNERS_LEFT_10,
  cornersHeader = CORNERS.CORNERS_LEFT_TOP_10,
}: IEpgSidebarProps): JSX.Element => {
  return (
    <SidebarComponent
      isOpen={isOpen}
      transitionName={transitionName}
      position={position}
      sidebarShadow={sidebarShadow}
      horizontalPaddings={horizontalPaddings}
      isLoading={isLoading}
      onClose={onClose}
      corners={corners}
    >
      <PopupHeaderSC cornersHeader={cornersHeader} notRelative>
        <EpgPoster posterUrl={channelLogoUrl!} />

        <TextBoxMaxLine1Headline3 title={channelName} maxLines={1} />
        {isCloseBtnShown && (
          <InlineCloseButton onClick={onClose} paddings={[INDENTS.INDENT_40]} />
        )}
      </PopupHeaderSC>
      <EpgSidebarBodySC>
        {Boolean(icon) && <PosterContainerBlur posterUrl={icon!} />}
        <EpgSidebarProgramInfoSC>
          <LabelGroup divider={Divider.Dot} columnGap={FIXED_INDENTS.INDENT_20}>
            {labels?.map((label: string, index) =>
              isLive && index === 0 ? (
                <LabelLive
                  title={label}
                  key={label}
                  font={LabelFonts.Caption4}
                />
              ) : (
                <Label title={label} key={label} font={LabelFonts.Caption4} />
              )
            )}
          </LabelGroup>

          <TextBox
            title={epgTitle}
            font={TextBoxTypes.Headline2}
            maxLines={2}
          />
          {Boolean(episode || subtitle) && (
            <TextBox
              title={
                episode && subtitle
                  ? `${episode}: ${subtitle}`
                  : subtitle
                    ? subtitle
                    : episode!
              }
              font={TextBoxTypes.Headline3}
              maxLines={2}
            />
          )}
          {Boolean(tags?.length) && (
            <TextBoxWithChildrenMaxLines2 verticalGap={FIXED_INDENTS.INDENT_40}>
              {tags.map(
                (tag, index) =>
                  tag && (
                    <TextBox
                      title={tag}
                      font={TextBoxTypes.Caption4}
                      color={Colors.TEXT_ICON_SECONDARY}
                      key={`${tag}${index}`}
                    />
                  )
              )}
            </TextBoxWithChildrenMaxLines2>
          )}
          {Boolean(programDescription) && (
            <TextBoxBody4 preWrap>{programDescription}</TextBoxBody4>
          )}
          {Boolean(directors?.length) && (
            <TextBox maxLines={2}>
              <TextBox
                title={titleDirectors}
                font={TextBoxTypes.Caption3}
                color={Colors.TEXT_ICON_SECONDARY}
              />
              <TextBox
                title={directors.join(', ')}
                font={TextBoxTypes.Caption3}
                color={Colors.TEXT_ICON_PRIMARY}
              />
            </TextBox>
          )}
          {Boolean(actors?.length) && (
            <TextBox maxLines={2}>
              <TextBox
                title={titleStars}
                font={TextBoxTypes.Caption3}
                color={Colors.TEXT_ICON_SECONDARY}
              />
              <TextBox
                title={actors.join(', ')}
                font={TextBoxTypes.Caption3}
                color={Colors.TEXT_ICON_PRIMARY}
              />
            </TextBox>
          )}
        </EpgSidebarProgramInfoSC>
        {Boolean(programList && programList.length) && (
          <>
            <TextBox font={TextBoxTypes.Headline3} title={titleUpcoming} />
            <ProgramEpgList>
              {programList?.map(({ title, subtitle, labels }, index) => (
                <ProgramEpgItem
                  title={title}
                  subtitle={subtitle}
                  labels={labels}
                  key={`${title}${index}`}
                />
              ))}
            </ProgramEpgList>
          </>
        )}
      </EpgSidebarBodySC>
    </SidebarComponent>
  )
}
